export const MAX_COMPARISON_PERIODS = 1;

const parseIdList = raw => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map(Number).filter(id => id > 0);
  }
  return String(raw)
    .split(',')
    .map(Number)
    .filter(id => id > 0);
};

const parseStringList = raw => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  return String(raw)
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
};

export const parseSummaryEntityFilterParams = query => ({
  agentIds: parseIdList(query.agent_ids),
  inboxIds: parseIdList(query.inbox_ids),
  teamIds: parseIdList(query.team_ids),
  labelIds: parseIdList(query.label_ids),
  excludeLabelIds: parseIdList(query.exclude_label_ids),
  channelTypes: parseStringList(query.channel_types),
  episodeResult: query.episode_result || 'all',
  receiptType: query.receipt_type || 'all',
  tagPresence: query.tag_presence || 'all',
  resolutionMethod: query.resolution_method || 'all',
  statusAtPeriodEnd: query.status_at_period_end || 'all',
  reopening: query.reopening || 'all',
  transfers: query.transfers || 'all',
  humanResponse: query.human_response || 'all',
});

export const generateSummaryEntityFilterParams = filters => {
  const params = {};
  if (filters.agentIds?.length) params.agent_ids = filters.agentIds.join(',');
  if (filters.inboxIds?.length) params.inbox_ids = filters.inboxIds.join(',');
  if (filters.teamIds?.length) params.team_ids = filters.teamIds.join(',');
  if (filters.labelIds?.length) params.label_ids = filters.labelIds.join(',');
  if (filters.excludeLabelIds?.length) {
    params.exclude_label_ids = filters.excludeLabelIds.join(',');
  }
  if (filters.channelTypes?.length) {
    params.channel_types = filters.channelTypes.join(',');
  }
  if (filters.episodeResult && filters.episodeResult !== 'all') {
    params.episode_result = filters.episodeResult;
  }
  if (filters.receiptType && filters.receiptType !== 'all') {
    params.receipt_type = filters.receiptType;
  }
  if (filters.tagPresence && filters.tagPresence !== 'all') {
    params.tag_presence = filters.tagPresence;
  }
  if (filters.resolutionMethod && filters.resolutionMethod !== 'all') {
    params.resolution_method = filters.resolutionMethod;
  }
  if (filters.statusAtPeriodEnd && filters.statusAtPeriodEnd !== 'all') {
    params.status_at_period_end = filters.statusAtPeriodEnd;
  }
  if (filters.reopening && filters.reopening !== 'all') {
    params.reopening = filters.reopening;
  }
  if (filters.transfers && filters.transfers !== 'all') {
    params.transfers = filters.transfers;
  }
  if (filters.humanResponse && filters.humanResponse !== 'all') {
    params.human_response = filters.humanResponse;
  }
  return params;
};

export const generateReportURLParams = ({
  from,
  to,
  businessHours,
  groupBy,
  range,
  comparisonPeriods,
  entityFilters,
}) => {
  const params = {};

  if (from) params.from = from;
  if (to) params.to = to;

  if (businessHours) params.business_hours = 'true';
  if (groupBy) params.group_by = groupBy;

  if (range) params.range = range;

  if (comparisonPeriods?.length) {
    params.comparison_periods = JSON.stringify(
      comparisonPeriods.slice(0, MAX_COMPARISON_PERIODS).map(p => ({
        since: p.from,
        until: p.to,
      }))
    );
  }

  return {
    ...params,
    ...generateSummaryEntityFilterParams(entityFilters || {}),
  };
};

export const parseComparisonPeriodsQueryValue = raw => {
  if (!raw || typeof raw !== 'string') return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .slice(0, MAX_COMPARISON_PERIODS)
      .map(p => ({
        from: Number(p.since ?? p.from),
        to: Number(p.until ?? p.to),
      }))
      .filter(p => p.from > 0 && p.to > 0);
  } catch {
    return [];
  }
};

export const parseReportURLParams = query => {
  const { from, to, business_hours, group_by, range, comparison_periods } =
    query;

  return {
    from: from ? Number(from) : null,
    to: to ? Number(to) : null,
    businessHours: business_hours === 'true',
    groupBy: group_by ? Number(group_by) : null,
    range: range || null,
    comparisonPeriods: parseComparisonPeriodsQueryValue(comparison_periods),
  };
};

export const parseDetailReportFilters = query => ({
  ...parseReportURLParams(query),
  ...parseSummaryEntityFilterParams(query),
});

// Parse filter params from URL (agent_id, inbox_id, team_id, sla_policy_id, label, rating)
export const parseFilterURLParams = query => {
  return {
    agent_id: query.agent_id ? Number(query.agent_id) : null,
    inbox_id: query.inbox_id ? Number(query.inbox_id) : null,
    team_id: query.team_id ? Number(query.team_id) : null,
    sla_policy_id: query.sla_policy_id ? Number(query.sla_policy_id) : null,
    label: query.label || null,
    rating: query.rating ? Number(query.rating) : null,
  };
};

// Generate filter URL params (only include non-null values)
export const generateFilterURLParams = filters => {
  const params = {};
  if (filters.agent_id) params.agent_id = filters.agent_id;
  if (filters.inbox_id) params.inbox_id = filters.inbox_id;
  if (filters.team_id) params.team_id = filters.team_id;
  if (filters.sla_policy_id) params.sla_policy_id = filters.sla_policy_id;
  if (filters.label) params.label = filters.label;
  if (filters.rating) params.rating = filters.rating;
  return params;
};

// Merge date range and filter params for complete URL
export const generateCompleteURLParams = ({
  from,
  to,
  range,
  filters = {},
  comparisonPeriods,
}) => {
  const dateParams = generateReportURLParams({
    from,
    to,
    range,
    comparisonPeriods,
  });
  const filterParams = generateFilterURLParams(filters);
  return { ...dateParams, ...filterParams };
};
